import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// ── Load modular locales dynamically via Vite eager glob imports ──────────────
const esModules = import.meta.glob<{ default: Record<string, unknown> } | Record<string, unknown>>(
  './locales/es/*.json',
  { eager: true }
);
const enModules = import.meta.glob<{ default: Record<string, unknown> } | Record<string, unknown>>(
  './locales/en/*.json',
  { eager: true }
);

const esResources: Record<string, Record<string, unknown>> = {};
const enResources: Record<string, Record<string, unknown>> = {};
const esTranslationBundle: Record<string, Record<string, unknown>> = {};
const enTranslationBundle: Record<string, Record<string, unknown>> = {};

for (const path in esModules) {
  const match = path.match(/\/([^/]+)\.json$/);
  if (match) {
    const ns = match[1];
    const mod = esModules[path];
    const data = (mod && 'default' in mod && mod.default ? mod.default : mod) as Record<string, unknown>;
    esResources[ns] = data;
    esTranslationBundle[ns] = data;
  }
}

for (const path in enModules) {
  const match = path.match(/\/([^/]+)\.json$/);
  if (match) {
    const ns = match[1];
    const mod = enModules[path];
    const data = (mod && 'default' in mod && mod.default ? mod.default : mod) as Record<string, unknown>;
    enResources[ns] = data;
    enTranslationBundle[ns] = data;
  }
}

// ── Canonical NAMESPACES list (Documentacion_Implementacion_i18n_Nexus.md § 3.1) ──
export const NAMESPACES = [
  'common',
  'nav',
  'user',
  'auth',
  'shell',
  'reportIssue',
  'connectors',
  'ontology',
  'pipelines',
  'agents',
  'humanActions',
  'settings',
  'apps',
  'workbench',
  'externalApps',
  'discovery',
  'taskMining',
  'botBuilder',
  'logic',
  'data',
  'activity',
  'value',
  'evals',
  'scenarios',
  'insights',
  'predictions',
  'operations',
  'admin',
  'superadmin',
  'platform',
  'projects',
  'finance',
  'utilities',
  'academy',
  'academyMaster',
  'academyLink',
  'alerts',
  'users',
  'audit',
  'schedules',
  'oncall',
  'process',
  'share',
  'quality',
  'health',
  'gateway',
  'lineage',
  'events',
  'explorer',
  'pdf',
  'workflow',
  'incidents',
  'mirror',
  'legal',
  'release',
] as const;

export type Namespace = typeof NAMESPACES[number];

// ── Product default language: 'es' (Documentacion_Implementacion_i18n_Nexus.md § 2.2 & 2.3) ──
const getSavedLang = (): string => {
  try {
    return localStorage.getItem('nexus_lang') || 'es';
  } catch {
    return 'es';
  }
};

const savedLang = getSavedLang();

i18n
  .use(initReactI18next)
  .init({
    resources: {
      es: {
        ...esResources,
        translation: esTranslationBundle,
      },
      en: {
        ...enResources,
        translation: enTranslationBundle,
      },
    },
    lng: savedLang,
    fallbackLng: 'en',
    defaultNS: 'common',
    fallbackNS: ['translation', 'common'],
    ns: [...NAMESPACES, 'translation'],
    interpolation: {
      escapeValue: false,
    },
  });

if (typeof document !== 'undefined') {
  document.documentElement.lang = savedLang;
  document.documentElement.setAttribute('lang', savedLang);
}

i18n.on('languageChanged', (lng) => {
  try {
    localStorage.setItem('nexus_lang', lng);
  } catch {
    // Ignore if storage is blocked
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng;
    document.documentElement.setAttribute('lang', lng);
  }
});

export default i18n;
